import { useQuery } from '@tanstack/react-query'
import { startOfWeek, sub } from 'date-fns'
import { useEffect, useMemo } from 'react'
import { useDataProvider } from 'src/adapters/HTTPClient'
import { useRealIdentity } from 'src/adapters/Identity'
import type { TimeSpentEventsMap } from 'src/App'
import { useCommandStore } from 'src/commandStore'
import { applyCommands } from 'src/commandStore/applyCommands'
import type { Command } from 'src/commandStore/types'

import type { TimeSpentEvent } from './types'
import { useSync } from './useSync'

export type CommandStore<T> = {
  state: T
  dispatch: (command: Command) => void
}

export type RootStore = CommandStore<TimeSpentEventsMap>

export function useRootStore(): RootStore {
  const me = useRealIdentity()
  const { events, queryKey } = useTimeSpentEvents(me?.id)
  const { commandList, addCommand, setCommandsStatus, deleteCommands } =
    useCommandStore()

  const localTimeSpentEvents = useMemo(() => {
    return applyCommands(events ?? {}, commandList)
  }, [events, commandList])

  useSync(commandList, {
    debounceTime: 3 * 1000,
    setCommandsStatus,
    deleteCommands,
    queryKey,
    commandList,
  })

  const hasWaitingCommands = useMemo(() => {
    return commandList.length > 0
  }, [commandList])
  useEffect(() => {
    if (!hasWaitingCommands) return

    const listener = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      return 'You have unsaved changes. Are you sure you want to leave?'
    }
    window.addEventListener('beforeunload', listener)

    return () => {
      window.removeEventListener('beforeunload', listener)
    }
  }, [hasWaitingCommands])

  return useMemo(() => {
    return {
      state: localTimeSpentEvents,
      dispatch: addCommand,
    }
  }, [localTimeSpentEvents, addCommand])
}

export function useTimeSpentEvents(userId: number | undefined) {
  const dataProvider = useDataProvider()
  const now = new Date()
  const past = sub(startOfWeek(now, { weekStartsOn: 1 }), { weeks: 4 })
  const queryKey = ['cockpit', 'TimeSpentEvent', 'getList', userId, past]
  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  const { data: events } = useQuery({
    queryKey,
    async queryFn() {
      const response = await dataProvider.getList<TimeSpentEvent>({
        resource: 'time_spent_events',
        filters: [
          { field: 'gitlabUserId', operator: 'eq', value: userId },
          { field: 'startDate', operator: 'gte', value: past },
        ],
      })
      return response
    },
    enabled: Boolean(userId),
    select(response) {
      return Object.fromEntries(response.data.map((item) => [item.id, item]))
    },
    keepPreviousData: true,
  })
  return { events: events ?? {}, queryKey }
}
